import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Save, Loader2 } from 'lucide-react';
import type {
  ReproveConfig,
  AnswerMap,
  ReprovePayload,
  TabScore,
} from '../../types/reprove';
import TabProgress from '../../components/TabProgress';
import TileButton from '../../components/shared/TileButton';
import { useReproveScoring } from '../../hooks/useReproveScoring';

type HeaderMeta = {
  representante: string;
  ciudad: string;
  puntoVenta: string;
  date: string;
};

type WizardProps = {
  config: ReproveConfig;
  onSave: (payload: ReprovePayload) => Promise<void>;
  headerMeta: HeaderMeta;
  onSurveyComplete?: () => void;
  isMobile?: boolean;
};

const EVAL_LABEL = 'EVALUACIÓN';

export function Wizard({
  config,
  onSave,
  headerMeta,
  onSurveyComplete,
  isMobile = false,
}: WizardProps): ReactElement {
  const [tab, setTab] = useState(1);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [comments, setComments] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const lastIndex = useMemo(
    () => config.tabs[config.tabs.length - 1]?.index ?? 1,
    [config.tabs]
  );
  const isEval = tab === lastIndex + 1;

  const [meta, setMeta] = useState({
    repId: '',
    cityId: '',
    pointId: '',
    date: '',
    hasSeller: false,
    sellerId: '',
    otcValue: '',
    sellerMetric: '',
    comments: '',
  });

  const { tabScores, totalPct, totalMaxPct } = useReproveScoring(
    config,
    answers
  );

  const allQuestionsAnswered = useMemo(() => {
    return config.tabs.every((tabConfig) => {
      const tabAnswers = answers[tabConfig.index] ?? {};
      return tabConfig.questions.every((question) => {
        const answer = tabAnswers[question.id];
        return answer === true || answer === false || answer === 'na';
      });
    });
  }, [config.tabs, answers]);

  useEffect(() => {
    if (isEval && allQuestionsAnswered && onSurveyComplete) {
      onSurveyComplete();
    }
  }, [isEval, allQuestionsAnswered, onSurveyComplete]);

  // Auto-avance cuando se completa un tab
  useEffect(() => {
    const current = config.tabs.find((t) => t.index === tab);
    if (!current) return;

    const a = answers[tab] ?? {};
    const complete =
      current.questions.length > 0 &&
      Object.keys(a).length === current.questions.length;

    if (complete) {
      const next = config.tabs.find((t) => t.index === tab + 1);
      setTimeout(() => {
        setTab(next ? tab + 1 : lastIndex + 1);
      }, 150);
    }
  }, [answers, tab, config.tabs, lastIndex]);

  function setAnswer(qid: string, val: true | false | 'na'): void {
    setAnswers((prev) => ({
      ...prev,
      [tab]: { ...(prev[tab] ?? {}), [qid]: val },
    }));
  }

  const current = useMemo(
    () => config.tabs.find((t) => t.index === tab),
    [config.tabs, tab]
  );

  const isCurrentComplete = useMemo(() => {
    if (!current) return false;
    const a = answers[tab] ?? {};
    return (
      current.questions.length > 0 &&
      current.questions.every((q) => {
        const answer = a[q.id];
        return answer === true || answer === false || answer === 'na';
      })
    );
  }, [answers, current, tab]);
  // DEBUG

  // END DEBUG
  const getBarColor = (s: TabScore): string => {
    const pct = (s.earnedPct / s.maxPct) * 100;
    if (pct === 0) return 'bg-red-500';
    if (pct < 34) return 'bg-red-500';
    if (pct < 67) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const evalTone = (
    t: number
  ): { text: string; border: string; bg: string } => {
    if (t >= 80)
      return {
        text: 'text-green-700',
        border: 'border-green-700',
        bg: 'bg-green-50',
      };
    if (t >= 50)
      return {
        text: 'text-yellow-700',
        border: 'border-yellow-600',
        bg: 'bg-yellow-50',
      };
    return { text: 'text-red-700', border: 'border-red-700', bg: 'bg-red-50' };
  };

  const tone = evalTone(totalPct);

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setSaveError(null);

    try {
      const payload: ReprovePayload = {
        meta: {
          ...meta,
          comments,
        },
        answers,
        scorePct: totalPct,
      };

      await onSave(payload);
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : 'Error inesperado al guardar'
      );
    } finally {
      setSaving(false);
    }
  };

  if (isMobile) {
    return (
      <div className="flex flex-col h-full bg-gray-50">
        {/* Progress Header */}
        <div className="bg-white border-b p-4 sticky top-0 z-10">
          <div className="flex items-center justify-center gap-2 mb-3">
            {config.tabs.map((t, i) => (
              <div
                key={t.index}
                className={`rounded-full transition-all ${
                  t.index === tab
                    ? 'bg-primary-dark w-3 h-3'
                    : t.index < tab
                    ? 'bg-primary-light w-2 h-2'
                    : 'bg-gray-300 w-2 h-2'
                }`}
              />
            ))}
            {isEval && <div className="bg-primary-dark w-3 h-3 rounded-full" />}
          </div>
          <div className="text-center mb-3">
            <div className="text-sm font-bold text-gray-800">
              {current?.title.toUpperCase() ?? EVAL_LABEL}
            </div>
            {!isEval && current && (
              <div className="text-xs text-gray-500 mt-1">
                Progreso:{' '}
                {tabScores
                  .find((s) => s.tabIndex === tab)
                  ?.earnedPct.toFixed(1) ?? 0}
                % de{' '}
                {(
                  tabScores.find((s) => s.tabIndex === tab)?.maxPct ??
                  current.weightPct
                ).toFixed(1)}
                %
              </div>
            )}
          </div>
          {!isEval && current && (
            <div className="px-2">
              <TabProgress
                pct={tabScores.find((s) => s.tabIndex === tab)?.earnedPct ?? 0}
                maxPct={
                  tabScores.find((s) => s.tabIndex === tab)?.maxPct ??
                  current.weightPct
                }
              />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Banner de metadatos (solo en evaluación) */}
          {isEval && (
            <div className="bg-white rounded-lg p-4 mb-4 space-y-3">
              <div>
                <div className="text-xs font-bold text-gray-600">
                  Representante
                </div>
                <div className="text-sm text-gray-900">
                  {headerMeta.representante}
                </div>
              </div>
              <div>
                <div className="text-xs font-bold text-gray-600">Ciudad</div>
                <div className="text-sm text-gray-900">{headerMeta.ciudad}</div>
              </div>
              <div>
                <div className="text-xs font-bold text-gray-600">
                  Punto de Venta
                </div>
                <div className="text-sm text-gray-900">
                  {headerMeta.puntoVenta}
                </div>
              </div>
              <div>
                <div className="text-xs font-bold text-gray-600">Fecha</div>
                <div className="text-sm text-gray-900">{headerMeta.date}</div>
              </div>
            </div>
          )}

          {/* Preguntas */}
          {!isEval && current && (
            <div className="space-y-3">
              {current.questions.map((q) => {
                const a = answers[tab] ?? {};
                return (
                  <div
                    key={q.id}
                    className="bg-white rounded-lg p-4 border border-gray-200"
                  >
                    <div className="text-sm text-gray-800 mb-4">{q.text}</div>
                    <div className="grid grid-cols-3 gap-2">
                      <TileButton
                        label="Sí"
                        onClick={() => {
                          setAnswer(q.id, true);
                        }}
                        variant={a[q.id] === true ? 'filled' : 'outline'}
                        color={a[q.id] === true ? 'primary' : 'gray'}
                        size="md"
                        width="full"
                        className="h-14"
                      />
                      <TileButton
                        label="No"
                        onClick={() => {
                          setAnswer(q.id, false);
                        }}
                        variant={a[q.id] === false ? 'filled' : 'outline'}
                        color={a[q.id] === false ? 'gray' : 'gray'}
                        size="md"
                        width="full"
                        className="h-14"
                      />
                      <TileButton
                        label="N/A"
                        onClick={() => {
                          setAnswer(q.id, 'na');
                        }}
                        variant={a[q.id] === 'na' ? 'filled' : 'outline'}
                        color={a[q.id] === 'na' ? 'gray' : 'gray'}
                        size="md"
                        width="full"
                        className={`h-14 ${
                          a[q.id] === 'na' ? 'bg-gray-400 text-white' : ''
                        }`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Evaluación */}
          {isEval && (
            <div className="space-y-4">
              {/* Barras por sección */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                {tabScores.map((s) => {
                  const percentage = (s.earnedPct / s.maxPct) * 100;
                  const color = getBarColor(s);
                  return (
                    <div key={s.tabIndex} className="mb-4 last:mb-0">
                      <div className="flex flex-col gap-1 mb-2">
                        <span className="text-xs font-medium text-gray-800">
                          {s.title.toUpperCase()}
                        </span>
                        <span className="text-xs text-gray-500">
                          <span
                            className={
                              s.earnedPct === 0 ? 'text-red-600 font-bold' : ''
                            }
                          >
                            {s.earnedPct.toFixed(1)}%
                          </span>
                          {' de '}
                          <span className="font-bold">
                            {s.maxPct.toFixed(1)}%
                          </span>
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${color}`}
                          style={{ width: `${percentage.toFixed(2)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Comentarios */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="text-xs font-medium text-gray-700 mb-2">
                  Comentarios:
                </div>
                <textarea
                  className="w-full h-32 border border-gray-300 rounded-md p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-medium focus:border-transparent"
                  placeholder="Escribe comentarios..."
                  value={comments}
                  onChange={(e) => {
                    setComments(e.target.value);
                    setMeta((prev) => ({ ...prev, comments: e.target.value }));
                  }}
                  disabled={saving}
                />
              </div>

              {/* Evaluación total */}
              <div
                className={`rounded-lg border-2 p-4 flex flex-col items-center gap-2 ${tone.bg} ${tone.border} ${tone.text}`}
              >
                <div className="font-semibold tracking-wide text-sm">
                  EVALUACIÓN FINAL
                </div>
                <div className="text-2xl font-bold">
                  {totalPct.toFixed(1)}%
                  <span className="text-gray-800 text-base font-bold ml-1">
                    de 100%
                  </span>
                </div>
              </div>

              {/* Error */}
              {saveError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="text-red-700 text-sm font-medium mb-1">
                    Error al guardar
                  </div>
                  <div className="text-red-600 text-xs">{saveError}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sticky Bottom Button */}
        <div className="bg-white border-t p-4 sticky bottom-0">
          {!isEval ? (
            <TileButton
              label="Siguiente"
              Icon={ArrowRight}
              iconPosition="right"
              onClick={() => {
                if (!isCurrentComplete) return;
                setTab(tab === lastIndex ? lastIndex + 1 : tab + 1);
              }}
              disabled={!isCurrentComplete}
              variant="filled"
              color={isCurrentComplete ? 'primary' : 'gray'}
              size="lg"
              width="full"
              className="h-14"
            />
          ) : (
            <TileButton
              label={saving ? 'Guardando...' : 'Guardar Reporte'}
              Icon={saving ? Loader2 : Save}
              iconPosition="left"
              onClick={handleSave}
              disabled={saving}
              variant="filled"
              color="primary"
              size="lg"
              width="full"
              className={`h-14 ${saving ? 'animate-pulse' : ''}`}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1">
      {/* Tabs */}
      <div className="flex gap-2 bg-white border-b border-gray-200 px-6 pt-3 overflow-x-auto scrollbar-hide">
        <div className="overflow-x-auto">
          {[
            ...config.tabs.map((t) => ({ key: t.index, label: t.title })),
            { key: lastIndex + 1, label: EVAL_LABEL },
          ].map((t) => {
            const k = typeof t.key === 'number' ? t.key : lastIndex + 1;
            const active = k === tab;
            return (
              <button
                key={String(k)}
                onClick={() => {
                  setTab(k);
                }}
                className={`px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-t border transition-colors whitespace-nowrap flex-shrink-0 ${
                  active
                    ? 'text-primary-dark border-primary-medium bg-[rgba(35,111,19,0.11)]'
                    : 'bg-white border-transparent text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-8 py-6">
        {/* Banner de metadatos (solo en evaluación) */}
        {isEval && (
          <div className="bg-gray-100/70 rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div>
              <div className="text-md font-bold text-gray-900 mb-1">
                Representante
              </div>
              <div className="text-md font-medium text-gray-900">
                {headerMeta.representante}
              </div>
            </div>
            <div>
              <div className="text-md font-bold text-gray-900 mb-1">Ciudad</div>
              <div className="text-md font-medium text-gray-900">
                {headerMeta.ciudad}
              </div>
            </div>
            <div>
              <div className="text-md font-bold text-gray-900 mb-1">
                Punto de Venta
              </div>
              <div className="text-md font-medium text-gray-900">
                {headerMeta.puntoVenta}
              </div>
            </div>
            <div>
              <div className="text-md font-bold text-gray-900 mb-1">Fecha</div>
              <div className="text-md font-medium text-gray-900">
                {headerMeta.date}
              </div>
            </div>
          </div>
        )}

        {/* Preguntas */}
        {!isEval && current && (
          <div className="relative bg-white rounded-xl border border-gray-200 p-6">
            <div className="mb-5">
              <div className="text-xs text-gray-600 mb-2 flex items-center justify-between">
                <span className="font-semibold">
                  {current.title.toUpperCase()}
                </span>
                <div className="w-full sm:w-64 md:w-72 lg:w-80">
                  <TabProgress
                    pct={
                      tabScores.find((s) => s.tabIndex === tab)?.earnedPct ?? 0
                    }
                    maxPct={
                      tabScores.find((s) => s.tabIndex === tab)?.maxPct ??
                      current.weightPct
                    }
                  />
                </div>
              </div>
            </div>

            <div className="grid gap-4">
              {current.questions.map((q) => {
                const a = answers[tab] ?? {};
                return (
                  <div
                    key={q.id}
                    className="flex items-center justify-between py-3"
                  >
                    <div className="text-gray-800 flex-1 mr-8">{q.text}</div>
                    <div className="flex items-center gap-4">
                      <TileButton
                        label="Sí"
                        onClick={() => {
                          setAnswer(q.id, true);
                        }}
                        variant={a[q.id] === true ? 'filled' : 'outline'}
                        color={a[q.id] === true ? 'primary' : 'gray'}
                        size="sm"
                        pad="sm"
                        className="min-w-[60px]"
                      />
                      <TileButton
                        label="No"
                        onClick={() => {
                          setAnswer(q.id, false);
                        }}
                        variant={a[q.id] === false ? 'filled' : 'outline'}
                        color={a[q.id] === false ? 'gray' : 'gray'}
                        size="sm"
                        pad="sm"
                        className="min-w-[60px]"
                      />
                      <TileButton
                        label="N/A"
                        onClick={() => {
                          setAnswer(q.id, 'na');
                        }}
                        variant={a[q.id] === 'na' ? 'filled' : 'outline'}
                        color={a[q.id] === 'na' ? 'gray' : 'gray'}
                        size="sm"
                        pad="sm"
                        className={`min-w-[60px] ${
                          a[q.id] === 'na'
                            ? 'bg-gray-400 text-white hover:bg-gray-500'
                            : ''
                        }`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Indicador de preguntas N/A */}
            {tabScores.find((s) => s.tabIndex === tab)?.naCount > 0 && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-xs text-gray-600">
                  {tabScores.find((s) => s.tabIndex === tab)?.naCount}{' '}
                  pregunta(s) marcada(s) como N/A
                  <span className="ml-2 text-gray-500">
                    (Peso ajustado:{' '}
                    {tabScores
                      .find((s) => s.tabIndex === tab)
                      ?.adjustedWeightPct?.toFixed(1)}
                    %)
                  </span>
                </div>
              </div>
            )}

            {/* Botón siguiente */}
            <div className="flex justify-end mt-8">
              <TileButton
                label=""
                Icon={ArrowRight}
                iconPosition="right"
                onClick={() => {
                  if (!isCurrentComplete) return;
                  setTab(tab === lastIndex ? lastIndex + 1 : tab + 1);
                }}
                disabled={!isCurrentComplete}
                variant="filled"
                color={isCurrentComplete ? 'primary' : 'gray'}
                radius="full"
                size="md"
                className="px-6"
              />
            </div>
          </div>
        )}

        {/* Evaluación Desktop */}
        {isEval && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6">
              {tabScores.map((s) => {
                const percentage = (s.earnedPct / s.maxPct) * 100;
                const color = getBarColor(s);
                return (
                  <div key={s.tabIndex} className="mb-4">
                    <div className="flex items-center justify-between text-sm text-gray-800 mb-1">
                      <span className="font-medium">
                        {s.title.toUpperCase()}
                      </span>
                      <span className="text-gray-500">
                        <span
                          className={
                            s.earnedPct === 0 ? 'text-red-600 font-bold' : ''
                          }
                        >
                          {s.earnedPct.toFixed(1)}%
                        </span>
                        {' | '}
                        <span className="font-bold">
                          {s.maxPct.toFixed(1)}%
                        </span>
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${color}`}
                        style={{ width: `${percentage.toFixed(2)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  Comentarios:
                </div>
                <textarea
                  className="w-full h-40 border border-gray-300 rounded-md p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-medium focus:border-transparent transition-all"
                  placeholder="Escribe comentarios..."
                  value={comments}
                  onChange={(e) => {
                    setComments(e.target.value);
                    setMeta((prev) => ({ ...prev, comments: e.target.value }));
                  }}
                  disabled={saving}
                />
              </div>

              <div
                className={`rounded-lg border-2 p-4 flex items-center justify-between transition-colors ${tone.bg} ${tone.border} ${tone.text}`}
              >
                <div className="font-semibold tracking-wide">EVALUACIÓN</div>
                <div className="text-2xl font-semibold">
                  {totalPct.toFixed(1)}%{' '}
                  <span className="text-gray-800 text-base font-bold">
                    | {totalMaxPct.toFixed(0)}%
                  </span>
                </div>
              </div>

              {saveError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="text-red-700 text-sm font-medium mb-1">
                    Error al guardar
                  </div>
                  <div className="text-red-600 text-xs">{saveError}</div>
                </div>
              )}

              <TileButton
                label={saving ? 'Guardando...' : 'Guardar reporte'}
                Icon={saving ? Loader2 : Save}
                iconPosition="left"
                onClick={handleSave}
                disabled={saving}
                variant="filled"
                color="primary"
                size="lg"
                width="full"
                className={saving ? 'animate-pulse' : ''}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
